import { FormControl } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import { useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { type Control, Controller } from "react-hook-form";

import { InputWrapper } from "../CredentialsDashboard/ui/InputWrapper";
import { ErrorHelperText } from "../PatientsDashboard/ui/ErrorHelper";
import UploadFile from "../UploadFile";
import type { AIPaymentFormFields } from "./CrudForms.zod";

const ALLOWED_FILE_TYPES = ["application/pdf"];

interface AIPaymentFormProps {
  control: Control<AIPaymentFormFields>;
  practices: PracticeMessage[];
  supportedSources: SourceMessage[];
  supportedPayers: CredentialsSupportedPayersMessage[];
  setFileToUpload: (file: File | null) => void;
  fileToUpload: File | null;
  setFileError: (error: string | null) => void;
  fileError: string | null;
}

export default function AIPaymentForm({
  control,
  practices,
  supportedSources,
  supportedPayers,
  setFileToUpload,
  fileToUpload,
  setFileError,
  fileError,
}: AIPaymentFormProps) {
  const theme = useTheme();
  return (
    <>
      <Typography variant="h5" sx={{ fontWeight: "600" }}>
        Payment Details
      </Typography>
      <Box sx={{ margin: `${theme.spacing(3)} 0` }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="practiceId"
              control={control}
              render={({
                field: { onChange, onBlur, value, ref },
                fieldState: { error },
              }) => (
                <FormControl sx={{ width: "100%" }}>
                  <InputWrapper>
                    <TextField
                      select
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value ?? ""}
                      name="practiceId"
                      inputRef={ref}
                      label="Practice"
                      variant="outlined"
                      fullWidth
                      required
                      InputLabelProps={{
                        shrink: true,
                      }}
                    >
                      {practices.map((practice) => (
                        <MenuItem
                          key={practice.wieldyId}
                          value={practice.wieldyId}
                        >
                          {practice.displayName}
                        </MenuItem>
                      ))}
                    </TextField>
                    <ErrorHelperText message={error?.message} />
                  </InputWrapper>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="documentLevelType"
              control={control}
              render={({
                field: { onChange, onBlur, value, ref },
                fieldState: { error },
              }) => (
                <FormControl sx={{ width: "100%" }}>
                  <InputWrapper>
                    <TextField
                      select
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value ?? ""}
                      name="documentLevelType"
                      inputRef={ref}
                      label="Document Level Type"
                      variant="outlined"
                      fullWidth
                      required
                      InputLabelProps={{
                        shrink: true,
                      }}
                    >
                      <MenuItem key="EOP_DHMO" value="EOP_DHMO">
                        EOP_DHMO
                      </MenuItem>
                      <MenuItem key="ERA_PDF" value="ERA_PDF">
                        ERA_PDF
                      </MenuItem>
                      <MenuItem key="EOP_PDF" value="EOP_PDF">
                        EOP_PDF
                      </MenuItem>
                    </TextField>
                    <ErrorHelperText message={error?.message} />
                  </InputWrapper>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="source"
              control={control}
              render={({
                field: { onChange, onBlur, value, ref },
                fieldState: { error },
              }) => (
                <FormControl sx={{ width: "100%" }}>
                  <InputWrapper>
                    <TextField
                      select
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value ?? ""}
                      name="source"
                      inputRef={ref}
                      label="Source"
                      variant="outlined"
                      fullWidth
                      required
                      InputLabelProps={{
                        shrink: true,
                      }}
                    >
                      {supportedSources.map((source) => (
                        <MenuItem key={source.id} value={source.name}>
                          {source.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <ErrorHelperText message={error?.message} />
                  </InputWrapper>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="payer"
              control={control}
              render={({
                field: { onChange, onBlur, value, ref },
                fieldState: { error },
              }) => (
                <FormControl sx={{ width: "100%" }}>
                  <InputWrapper>
                    <TextField
                      select
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value ?? ""}
                      name="payer"
                      inputRef={ref}
                      label="Payer"
                      variant="outlined"
                      fullWidth
                      required
                      InputLabelProps={{
                        shrink: true,
                      }}
                    >
                      {supportedPayers.map((payer) => (
                        <MenuItem key={payer.wieldyId} value={payer.name}>
                          {payer.displayLabel}
                        </MenuItem>
                      ))}
                    </TextField>
                    <ErrorHelperText message={error?.message} />
                  </InputWrapper>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <ErrorHelperText message={fileError || undefined} />
            <UploadFile
              file={fileToUpload}
              setFile={setFileToUpload}
              onError={setFileError}
              allowedFileTypes={ALLOWED_FILE_TYPES}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
}
